// App.js — Bond v2.9.x (clean overlay + auth-gate + Friends view)

import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import "./App.css";
import {
  socket,
  on,
  createSession,
  joinSession,
  leaveSession,
  mediaCommand,
  setMediaSource,
} from "./lib/sessionApi";

import VideoPlayer from "./components/VideoPlayer";
import { PeerManager } from "./lib/rtc";
import ParticipantsLayout from "./components/ParticipantsLayout";
import TopBar from "./components/TopBar";
import { useAuth } from "./AuthContext";
import FriendsPage from "./pages/FriendsPage";
import ProvidersPage from "./pages/ProvidersPage";

import ContentPickerPanel from "./components/ContentPickerPanel";
import { PROVIDER_CATALOG } from "./lib/providerCatalog";


import SessionInvitePanel from "./components/SessionInvitePanel";
import {
  apiGetFriends,
  apiCreateSessionInvites,
  apiCreateSessionFromFriends,
    apiGetMyProviders,        // ⬅️ nieuw

} from "./lib/authApi";
import BondMiniInvitePanel from "./components/BondMiniInvitePanel";
import ChatPanel from "./components/ChatPanel";
import AuthCallbackPage from "./pages/AuthCallbackPage";


import designBg from "./assets/PastelGradientBackground.png";

import { createPortal } from "react-dom";



/** =========== Tunables (met profielen) =========== **/
const PING_INTERVAL_MS = 10000;

const PROFILES = {
  mp4: {
    LOOP_TICK_MS: 170,
    SOFT_SEEK_S: 1.2,
    SEEK_COOLDOWN_MS: 5500,
    KP: 0.22,
    KI: 0.08,
    MAX_RATE_DELTA_BASE: 0.009,
    MAX_RATE_DELTA_BOOST: 0.016,
    RATE_DECAY_MS: 750,
    JITTER_FILTER: 0.22,
    MIN_BUFFER_OK_S: 1.0,
    BOOST_WINDOW_MS: 600,
  },

 // HLS Smooth (default): minder nerveuze rate nudges → vloeiender sportbeeld
hlsSmooth: {
  LOOP_TICK_MS: 140,
  SOFT_SEEK_S: 0.60,
  SEEK_COOLDOWN_MS: 3000,
  KP: 0.35,
  KI: 0.07,
  MAX_RATE_DELTA_BASE: 0.012,
  MAX_RATE_DELTA_BOOST: 0.025,
  RATE_DECAY_MS: 900,
  JITTER_FILTER: 0.18,
  MIN_BUFFER_OK_S: 2.5,
  BOOST_WINDOW_MS: 1200,
},

// HLS Low-latency (optioneel, niet default; handig voor dev/test)
hlsLL: {
  LOOP_TICK_MS: 120,
  SOFT_SEEK_S: 0.35,
  SEEK_COOLDOWN_MS: 1500,
  KP: 0.55,
  KI: 0.12,
  MAX_RATE_DELTA_BASE: 0.03,
  MAX_RATE_DELTA_BOOST: 0.06,
  RATE_DECAY_MS: 500,
  JITTER_FILTER: 0.25,
  MIN_BUFFER_OK_S: 2.0,
  BOOST_WINDOW_MS: 1500,
},

};

// Telemetry window
const TELEMETRY_WINDOW = 400;

// helper: seconds → milliseconds (voor HUD)
const ms = (x) => Math.round((Number.isFinite(x) ? x : 0) * 1000);


/** =========== Component =========== **/
export default function App() {
  const {
  currentUser,
  token,
  initializing,
  login,
  signup,
  requestMagicLogin,
  completeMagicLogin,
  logout,
} = useAuth();


// Welke "pagina" je ziet
const [view, setView] = useState("landing"); // "landing" | "friends" | "session" | "providers"

  const [session, setSession] = useState(null);
  const [sessionState, setSessionState] = useState(null);
  const [isHost, setIsHost] = useState(false);
    const [joinToast, setJoinToast] = useState(null);
  const joinToastTimeoutRef = useRef(null);

  // ✅ Kicked toast (voor linked-share / provider kicks)
const [kickToast, setKickToast] = useState(null);
const kickToastTimeoutRef = useRef(null);
const kickedCleanupTimeoutRef = useRef(null);


const showKickToast = useCallback((text) => {
  setKickToast({ id: Date.now(), text: text || "Je bent uit de sessie gehaald." });

  if (kickToastTimeoutRef.current) clearTimeout(kickToastTimeoutRef.current);
  kickToastTimeoutRef.current = setTimeout(() => {
    setKickToast(null);
  }, 3200);
}, []);


const cleanupAfterKicked = useCallback(() => {
  setSession(null);
  setSessionState(null);
  setPeerCount(0);
  setIsHost(false);
  setView("friends");
  setChatMessages([]);
  setShowChat(false);
  setChatUnread(0);
  setChatPeekNonce(0);

  sessionPeersReadyRef.current = false;
  lastPeerIdsRef.current = new Set();
}, []);


  // Voor join-detectie via session:state
  const lastPeerIdsRef = useRef(new Set());
  const sessionPeersReadyRef = useRef(false);

  const [joinCode, setJoinCode] = useState("");
  const [sessionName, setSessionName] = useState("");
  const [serverVersion, setServerVersion] = useState("");
  const [source, setSource] = useState({
    mode: "hls",
    url: "https://test-streams.mux.dev/x36xhzz/x36xhzz.m3u8",
  });

  // Friends-data (voor nu nog lege arrays, API komt later / zit deels in FriendsPage)
  const [friends, setFriends] = useState([]);
  const [invites, setInvites] = useState([]);
  const [friendRequestsIncoming, setFriendRequestsIncoming] = useState([]);

    // Mijn gekoppelde pakketten / providers
  const [myProviders, setMyProviders] = useState([]);
  const [myMaxPackageLevel, setMyMaxPackageLevel] = useState(0);


  // Landing: knop pas tonen ná animatie
  const [showLandingButton, setShowLandingButton] = useState(false);

  // Auth modal state
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState("login"); // "login" | "signup"
  const [pendingAction, setPendingAction] = useState(null); // bv. "open-friends"

    // Content-paneel (wedstrijd/kanaal kiezen vóór sessie-start)
  const [showContentPicker, setShowContentPicker] = useState(false);
  const [pendingSessionPlan, setPendingSessionPlan] = useState(null);
  // pendingSessionPlan: { type: "solo" } of { type: "invite", friendIds: string[] }

  // Actief profiel op basis van bron
 const profile = useMemo(
  () => (source?.mode === "hls" ? PROFILES.hlsSmooth : PROFILES.mp4),
  [source]
);


  // UI
  const [isFs, setIsFs] = useState(false);
  const [zoomFill, setZoomFill] = useState(false);
  const [showHud, setShowHud] = useState(false);
  const [soundEnabled] = useState(true); // remote audio aan, geen toggle-knop
  const [videoOnly, setVideoOnly] = useState(false); // fullscreen zonder tiles
   // NIEUW: self view (eigen camtile tonen/verbergen)
  const [showSelfView, setShowSelfView] = useState(true);

  
  // Video-audio (mixer linksonder)
  const [videoMuted, setVideoMuted] = useState(false); // start NIET muted
  const [videoVolume, setVideoVolume] = useState(1);

   // Chat
  const [showChat, setShowChat] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);

  // ✅ nieuw: unread + peek trigger
const [chatUnread, setChatUnread] = useState(0);
const [chatPeekNonce, setChatPeekNonce] = useState(0);
const showChatRef = useRef(false);

  // Overlay (mixer + knoppen) auto-hide
  const [overlayVisible, setOverlayVisible] = useState(true);
  const overlayTimeoutRef = useRef(null);
const [showBondMini, setShowBondMini] = useState(false);
const [bondMiniFriends, setBondMiniFriends] = useState([]);
const [bondMiniBusy, setBondMiniBusy] = useState(false);
const [showMiniInvite, setShowMiniInvite] = useState(false);

  // click-outside refs voor panels in de sessie
  const invitePanelWrapperRef = useRef(null);
  const miniInviteWrapperRef = useRef(null);


  // refs
  const videoRef = useRef(null);
  // Tab visibility guard (voorkomt PI chaos bij background throttling)
const tabHiddenRef = useRef(false);
// Resume guard: na visibility resume / forceAlign even geen PI-tick (voorkomt haperingen)
const resumeGuardUntilRef = useRef(0);


  // ✅ Fase 2: live-HLS guardrails data vanuit VideoPlayer
const liveMetricsRef = useRef({ liveEdge: null, bufStart: null, bufEnd: null, now: 0 });

const rtcStartGenRef = useRef(0);     // generatie-counter (anti-race)
const rtcStartingRef = useRef(false); // simpele lock


// ✅ safeSeek functie (geleverd door VideoPlayer)
const safeSeekFnRef = useRef(null);


function safeSeek(t) {
  const fn = safeSeekFnRef.current;
  const v = videoRef.current;
  if (!v) return;

  if (typeof fn === "function") {
    fn(t);
    return;
  }

  // fallback (zou zelden gebeuren): direct seek
  try {
    v.fastSeek ? v.fastSeek(t) : (v.currentTime = t);
  } catch {}
}

function emitHostProgress(socket, session, videoEl, isHost) {
  if (!socket || !session?.code || !videoEl || !isHost) return;

  const t = Number.isFinite(videoEl.currentTime)
    ? videoEl.currentTime
    : 0;

  const playing = !videoEl.paused && !videoEl.ended;

  socket.emit(
    "media:progress",
    { t, playing },
    () => {} // fire-and-forget
  );
}


  const stageRef = useRef(null);
  const suppressRef = useRef(false);


  // server clock sync (epoch-based)
  const [serverOffsetMs, setServerOffsetMs] = useState(0);
  const [bestRttMs, setBestRttMs] = useState(null);

  // media state & peers (count uit session:state)
  const mediaStateRef = useRef(null);
  const lastAnchorIdRef = useRef(null);

  const lastSeqRef = useRef(0);
  const [peerCount, setPeerCount] = useState(0);

  // PI loop state
  const loopTimerRef = useRef(null);
  const lastHardSeekAtRef = useRef(0);
  const integErrRef = useRef(0);
  const filtErrRef = useRef(0);

  // Boost window
  const lastFragBoostUntilRef = useRef(0);

  // Telemetry
  const hardSeeksRef = useRef(0);
  const driftSamplesRef = useRef([]); // in seconds


  function calcP95Seconds(samples) {
  if (!samples || samples.length === 0) return 0;
  const sorted = samples.slice().sort((a, b) => a - b);
  return sorted[Math.min(sorted.length - 1, Math.floor(0.95 * sorted.length))] || 0;
}

function bufferHeadroomSec(videoEl) {
  if (!videoEl || !videoEl.buffered) return 0;
  const t = videoEl.currentTime || 0;
  for (let i = 0; i < videoEl.buffered.length; i++) {
    const start = videoEl.buffered.start(i);
    const end = videoEl.buffered.end(i);
    if (t >= start && t <= end) return Math.max(0, end - t);
  }
  return 0;
}


  // Initial align flags
  const needInitialAlignRef = useRef(false);
  const didInitialAlignRef = useRef(false);

  // === WebRTC peers ===
  const [localStream, setLocalStream] = useState(null);
  const peerStreamsRef = useRef(new Map()); // peerId -> MediaStream
  const [, forceRerender] = useState(0);
  const peerMgrRef = useRef(null);
  const localStreamRef = useRef(null);

  // 🔊 Unlock remote audio once after a user gesture (join/start click)
const audioUnlockedRef = useRef(false);

const unlockRemoteAudioOnce = () => {
  if (audioUnlockedRef.current) return;
  audioUnlockedRef.current = true;

  // best-effort: browser unlock na user gesture
  const audios = Array.from(document.querySelectorAll("audio"));
  for (const a of audios) {
    try { a.play?.(); } catch {}
  }

  console.log("[Audio] remote audio unlocked");
};



// Self-view (verberg mijn camtile)


  const [sessionFriends, setSessionFriends] = useState([]);
  const [sessionFriendsLoading, setSessionFriendsLoading] = useState(false);
  const [showInvitePanel, setShowInvitePanel] = useState(false);
  const [sessionInviteBusy, setSessionInviteBusy] = useState(false);
  // Welke content-tiers deze user mag starten
  const [entitlements, setEntitlements] = useState({
    free: true,       // Free altijd aan
    sports: false,
    allAccess: false,
  });


  /** ======== Version ======== **/
  useEffect(() => {
    const offVer = on("server:version", (v) => setServerVersion(v));
    return () => offVer();
  }, []);

  /** ======== Landing: button na animatie ======== **/
  useEffect(() => {
    if (view !== "landing") {
      setShowLandingButton(false);
      return;
    }
    setShowLandingButton(false);
    const id = setTimeout(() => setShowLandingButton(true), 1800);
    return () => clearTimeout(id);
  }, [view]);

  /** ======== Fullscreen ======== **/
  useEffect(() => {
    const handler = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);
  const toggleFullscreen = () => {
    const el = stageRef.current;
    if (document.fullscreenElement) document.exitFullscreen?.();
    else el?.requestFullscreen?.();
  };

useEffect(() => {
  if (session && session.code) {
    // elke nieuwe sessie start met self-view AAN
    setShowSelfView(true);
  }
}, [session && session.code]);

  /** ======== Socket ↔ DB-user binden (auth:bind) ======== **/
  useEffect(() => {
    if (!token || !socket) return;

    socket.emit("auth:bind", { token }, (res) => {
      if (!res) return;
      if (res.ok) {
        console.log("[auth:bind] socket is gekoppeld aan user", res.user);
      } else {
        console.warn("[auth:bind] mislukte bind:", res.error);
      }
    });
  }, [token]);

  /** ======== Abonnementen -> entitlements voor content ======== **/
  useEffect(() => {
  if (!token) {
    // uitgelogd → alleen Free
    setEntitlements({ free: true, sports: false, allAccess: false });
    return;
  }

  let cancelled = false;

  (async () => {
    try {
      const res = await apiGetMyProviders(token);
      if (cancelled) return;

      // Verwacht: [{ providerId: "sports" }, { providerId: "all_access" }, ...]
      const subs = Array.isArray(res?.providers)
        ? res.providers
        : Array.isArray(res?.items)
        ? res.items
        : [];

      let hasSports = false;
      let hasAllAccess = false;

      for (const s of subs) {
        const pid = String(s?.providerId || "").toLowerCase();

        if (pid === "sports") hasSports = true;
        if (pid === "all_access") hasAllAccess = true;
      }

      // AllAccess mag ook Sports-kanalen openen
      setEntitlements({
        free: true,
        sports: hasSports || hasAllAccess,
        allAccess: hasAllAccess,
      });
    } catch (e) {
      console.warn("[Bond] kon abonnementen niet laden", e);
      setEntitlements({ free: true, sports: false, allAccess: false });
    }
  })();

  return () => {
    cancelled = true;
  };
}, [token]);



  /** ======== Clock offset ======== **/
  useEffect(() => {
    if (!socket) return;
    const ping = () => {
      const t0 = Date.now();
      socket.emit("time:ping", null, (reply) => {
        const t3 = Date.now();
        const rtt = t3 - t0;
        const serverNow =
          typeof reply?.serverNow === "number" ? reply.serverNow : null;
        if (serverNow == null) return;
        const offset = serverNow - (t0 + rtt / 2);
        setServerOffsetMs((prev) =>
          bestRttMs === null || rtt < bestRttMs ? offset : prev
        );
        setBestRttMs((prev) => (prev === null ? rtt : Math.min(prev, rtt)));
      });
    };
    ping();
    const id = setInterval(ping, PING_INTERVAL_MS);
    return () => clearInterval(id);
  }, [socket, bestRttMs]);


  
  const showJoinToast = useCallback((name) => {
    if (!name) return;

    setJoinToast({ id: Date.now(), name });

    if (joinToastTimeoutRef.current) {
      clearTimeout(joinToastTimeoutRef.current);
    }

    joinToastTimeoutRef.current = setTimeout(() => {
      setJoinToast(null);
    }, 2600);
  }, []);



  /** ======== Session state + join-toast met username ======== **/
useEffect(() => {
  const offState = on("session:state", (s) => {
    setSessionState(s);

    const peers = Array.isArray(s?.peers) ? s.peers : [];
    setPeerCount(peers.length);

    if (s?.mediaSource) setSource(s.mediaSource);
    if (s?.mediaState) mediaStateRef.current = s.mediaState;

    

    const idsNow = new Set(peers.map((p) => String(p.socketId || p.id || "")));

    if (!sessionPeersReadyRef.current) {
      sessionPeersReadyRef.current = true;
      lastPeerIdsRef.current = idsNow;
      console.log("[Bond] baseline peers", Array.from(idsNow));
      return;
    }

    const last = lastPeerIdsRef.current || new Set();

    idsNow.forEach((id) => {
      if (!id) return;
      if (last.has(id)) return;

      const p = peers.find((pp) => String(pp.socketId || pp.id || "") === id);
      if (!p) return;

      const joinedUser = p.user || null;

      if (currentUser && joinedUser?.id) {
        if (String(joinedUser.id) === String(currentUser.id)) {
          console.log("[Bond] nieuwe peer is ikzelf → geen toast");
          return;
        }
      }

      const name = joinedUser?.name || joinedUser?.email || "Nieuwe deelnemer";
      console.log("[Bond] join-detected voor user:", name, "socket:", id);
      showJoinToast(name);
    });

    lastPeerIdsRef.current = idsNow;
  });

  const offEnd = on("session:ended", () => {
    setSession(null);
    setSessionState(null);
    setPeerCount(0);
    setIsHost(false);
    setView("friends");
    setChatMessages([]);
    setShowChat(false);
    setChatUnread(0);
    setChatPeekNonce(0);

    sessionPeersReadyRef.current = false;
    lastPeerIdsRef.current = new Set();
  });






  const offKicked = on("session:kicked", (_p) => {
  // Fullscreen netjes verlaten (mag blijven)
  if (document.fullscreenElement) {
    try {
      document.exitFullscreen?.();
    } catch {}
  }

  // ✅ Altijd dezelfde duidelijke Bond-melding
  showKickToast(
    "Degene waarmee je gekoppeld bent voor deze content heeft de sessie verlaten. Daarom ben jij ook uit de sessie gehaald."
  );

  // ✅ Eventuele oude cleanup-timeout annuleren
  if (kickedCleanupTimeoutRef.current) {
    clearTimeout(kickedCleanupTimeoutRef.current);
    kickedCleanupTimeoutRef.current = null;
  }

  // ✅ Cleanup na 1–2 frames (toast kan eerst renderen)
  kickedCleanupTimeoutRef.current = setTimeout(() => {
    cleanupAfterKicked(); // of inline cleanup, zoals jij al had
    kickedCleanupTimeoutRef.current = null;
  }, 60);
});



  return () => {
    offState();
    offEnd();
    offKicked();


   // ✅ extra: kick-cleanup timeout opruimen
  if (kickedCleanupTimeoutRef.current) {
    clearTimeout(kickedCleanupTimeoutRef.current);
    kickedCleanupTimeoutRef.current = null;
  }
};

}, [showJoinToast, currentUser, showKickToast, cleanupAfterKicked]);







    /** ======== Chat-berichten (socket → state) ======== **/
  useEffect(() => {
  const off = on("chat:message", (msg) => {
setChatMessages((prev) => {
  // probeer naam uit msg zelf
  const directName =
    msg?.user?.name ||
    msg?.user?.email ||
    msg?.name;

  // anders: lookup in sessionState.peers op socketId
  let peerName = null;
  const peers = Array.isArray(sessionState?.peers) ? sessionState.peers : [];
  const sid = String(msg?.socketId || msg?.from || msg?.peerId || "");
  if (!directName && sid) {
    const peer = peers.find(
      (p) => String(p.socketId || p.id || "") === sid
    );
    peerName = peer?.user?.name || peer?.user?.email || null;
  }

  const finalName = directName || peerName || "guest";

  return [...prev, { ...msg, displayName: finalName }];
});

    // als chat dicht is → unread + peek
    if (!showChatRef.current) {
      setChatUnread((c) => {
        if (c === 0) {
          // peek alleen bij eerste nieuwe bericht in de burst
          setChatPeekNonce((n) => n + 1);
        }
        return c + 1;
      });
    }
  });
  return () => off();
}, []);


  useEffect(() => {
  showChatRef.current = showChat;
  if (showChat) setChatUnread(0);
}, [showChat]);


/** ======== Chat history (server → state) ======== **/
useEffect(() => {
  const off = on("chat:history", (history) => {
    const arr = Array.isArray(history) ? history : [];

    // Optional: sort by ts just in case
    arr.sort((a, b) => (a?.ts || 0) - (b?.ts || 0));

    setChatMessages(arr);

    // history telt niet als "unread"
    setChatUnread(0);
    // geen peek trigger hier
  });

  return () => off();
}, []);


  /** ======== Media state van server: guests volgen (host niet) ======== **/
  useEffect(() => {
const off = on("media:state", async (s) => {
      mediaStateRef.current = s;
      // ✅ nieuw: anchor-reset (play/seek/source switch)
const incomingAnchor = s?.anchorId || null;
if (incomingAnchor && incomingAnchor !== lastAnchorIdRef.current) {
  lastAnchorIdRef.current = incomingAnchor;

  // Forceer éénmalige harde align op het nieuwe anker
  needInitialAlignRef.current = true;
  didInitialAlignRef.current = false;

  // Reset PI-state (voorkomt rare drift na seek/reconnect)
  integErrRef.current = 0;
  filtErrRef.current = 0;

  // Reset hard-seek cooldown zodat align meteen mag
  lastHardSeekAtRef.current = 0;

  // Kleine boost window om sneller strak te trekken
  lastFragBoostUntilRef.current = Date.now() + profile.BOOST_WINDOW_MS;
}





      if (isHost) return; // host corrigeert niet
      const v = videoRef.current;
      if (!v || !s) return;
      if (typeof s.seq === "number" && s.seq <= lastSeqRef.current) return;
      if (typeof s.seq === "number") lastSeqRef.current = s.seq;

      const clientNow = Date.now();
      const serverNowEstimate = clientNow + serverOffsetMs;
      const elapsedS = Math.max(0, (serverNowEstimate - s.serverEpoch) / 1000);
      const target = s.playing ? s.mediaTime + elapsedS : s.mediaTime;

      // Alleen initial align doen; daarna PI-loop laten sturen
            suppressRef.current = true;

      if (!didInitialAlignRef.current && needInitialAlignRef.current) {
        try { safeSeek(target); } catch {}

        // ✅ hard-align telt als hard seek: zet cooldown + reset PI-state
        lastHardSeekAtRef.current = Date.now();
        integErrRef.current = 0;
        filtErrRef.current = 0;

        // ✅ na een resume/join even PI uit (voorkomt micro-hapering)
        resumeGuardUntilRef.current = Date.now() + 900;
        lastFragBoostUntilRef.current = Date.now() + profile.BOOST_WINDOW_MS;


        try { if (s.playing) await v.play(); else v.pause(); } catch {}

        needInitialAlignRef.current = false;
        didInitialAlignRef.current = true;
      } else {
        try { if (s.playing) v.play().catch(() => {}); else v.pause(); } catch {}
      }

      suppressRef.current = false;
    });
    return () => off();
  }, [serverOffsetMs, isHost]);

  /** ======== Host → server ======== **/
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !session || !isHost) return;

    const onPlay = async () => {
      if (suppressRef.current) return;
      try {
        await v.play();
      } catch {}
await mediaCommand({ type: "play", t: v.currentTime });
    };
const onSeek = async () => {
      if (suppressRef.current) return;
await mediaCommand({ type: "seek", t: v.currentTime });
    };
   const onPause = async () => {
  if (suppressRef.current) return;
  // Host pause expliciet melden (server beslist wat mag)
  await mediaCommand({ type: "pause", t: v.currentTime });
};


    v.addEventListener("play", onPlay);
    v.addEventListener("seeked", onSeek);
    v.addEventListener("pause", onPause);
    return () => {
      v.removeEventListener("play", onPlay);
      v.removeEventListener("seeked", onSeek);
      v.removeEventListener("pause", onPause);
    };
  }, [session, isHost]);

  /** ======== Host progress → server (authoritative heartbeat) ======== **/
useEffect(() => {
  if (!isHost || !session || !socket) return;

  const v = videoRef.current;
  if (!v) return;

  // 1× per seconde is ruim voldoende en schaalbaar
  const id = setInterval(() => {
// Always-play mode: blijf progress sturen zolang video echt speelt
if (v.paused || v.ended) return;
  emitHostProgress(socket, session, v, true);
}, 1000);


  // Direct ook 1× pushen bij start
if (!tabHiddenRef.current) emitHostProgress(socket, session, v, true);

  return () => clearInterval(id);
}, [isHost, session, socket]);


// Helper: sessie joinen met nette foutmelding (incl. NO_ACCESS_PROVIDER)
async function joinSessionWithFeedback(code) {
  if (!code) return null;

  try {
const res = await joinSession(code, token);

    if (!res || !res.ok) {
      const err = res?.error;

      if (err === "NO_SUCH_CODE") {
        window.alert("Deze sessie bestaat niet meer.");
        return null;
      }

      if (err === "NO_ACCESS_PROVIDER") {
        const pid = res?.providerId;
        let label = "juiste";

        if (pid === "sports") label = "Sports";
        else if (pid === "all_access") label = "All Access";
        else if (pid === "free") label = "Free";

        window.alert(
          `Je hebt het ${label}-abonnement nodig om aan deze sessie mee te doen.`
        );
        return null;
      }

      window.alert("Kon sessie niet joinen. Probeer het later nog eens.");
      return null;
    }

    return res;
  } catch (e) {
    console.warn("[Bond] joinSessionWithFeedback error", e);
    window.alert("Kon sessie niet joinen. Probeer het later nog eens.");
    return null;
  }
}



 /** ======== Auto-join via ?join= ======== **/
useEffect(() => {
  const params = new URLSearchParams(window.location.search);
  const code = (params.get("join") || params.get("code") || "").toUpperCase();
  if (code) {
    setJoinCode(code);
    (async () => {
      const res = await joinSessionWithFeedback(code);
      if (res) {
        setSession(res);
        setIsHost(!!res.isHost);
        setView("session");
      }
    })();
  }
}, []);


async function forceAlign(label = "manual") {

  // Reset controller energie bij hard re-anchor (tab resume / vis)
integErrRef.current = 0;
filtErrRef.current = 0;

  const v = videoRef.current;
  if (!v) {
    console.log("[forceAlign] no video", label);
    return;
  }

  // Host: alleen play herstellen
   // ✅ Host: bij resume altijd authoritative rebase op serverEpoch
  if (isHost) {
    let s = mediaStateRef.current;

    // pull nieuwste state
    if (session?.code && socket) {
      try {
        const pulled = await new Promise((res) =>
          socket.emit("media:pull", { code: session.code }, res)
        );
        if (pulled?.ok && pulled?.state) {
          s = pulled.state;
          mediaStateRef.current = s;
        }
      } catch {}
    }

    if (!s) {
      try { await v.play(); } catch {}
      return;
    }

    const now = Date.now();
    const elapsed = Math.max(0, (now - s.serverEpoch) / 1000);
    const target = s.playing ? s.mediaTime + elapsed : s.mediaTime;

    suppressRef.current = true;
    try { safeSeek(target); } catch {}

    // ✅ markeer als hard align + PI guard (voor het geval je later host PI toevoegt)
    lastHardSeekAtRef.current = Date.now();
    integErrRef.current = 0;
    filtErrRef.current = 0;
    resumeGuardUntilRef.current = Date.now() + 900;

    try { if (s.playing) await v.play(); else v.pause(); } catch {}
    suppressRef.current = false;
    return;
  }



  console.log("[forceAlign] start", label, {
    vis: document.visibilityState,
    hasSession: !!session?.code,
    hasState: !!mediaStateRef.current,
    t: v.currentTime,
  });

  // 1) Pull latest authoritative state (als jouw media:pull al bestaat)
  let s = mediaStateRef.current;
  if (session?.code && socket) {
    try {
      const pulled = await new Promise((resolve) => {
        socket.emit("media:pull", { code: session.code }, (res) => resolve(res));
      });
      console.log("[forceAlign] pulled", label, pulled);

      if (pulled?.ok && pulled?.state) {
        s = pulled.state;
        mediaStateRef.current = pulled.state;
      }
    } catch (e) {
      console.log("[forceAlign] pull error", label, e);
    }
  }

  if (!s) {
    console.log("[forceAlign] no state", label);
    try { await v.play(); } catch {}
    return;
  }

  // 2) Target berekenen
  const clientNow = Date.now();
  const serverNowEstimate = clientNow + serverOffsetMs;
  const elapsedS = Math.max(0, (serverNowEstimate - s.serverEpoch) / 1000);
  const target = s.playing ? s.mediaTime + elapsedS : s.mediaTime;

  console.log("[forceAlign] target", label, {
    playing: s.playing,
    mediaTime: s.mediaTime,
    serverEpoch: s.serverEpoch,
    serverOffsetMs,
    elapsedS,
    target,
    before: v.currentTime,
  });

  // 3) Seek uitvoeren
  // 3) Seek uitvoeren
suppressRef.current = true;

try { safeSeek(target); } catch {}

// ✅ markeer als hard align + reset controller
lastHardSeekAtRef.current = Date.now();
integErrRef.current = 0;
filtErrRef.current = 0;
resumeGuardUntilRef.current = Date.now() + 900;

try {
  if (s.playing) await v.play();
  else v.pause();
} catch {}

suppressRef.current = false;



  setTimeout(() => {
    const vv = videoRef.current;
    console.log("[forceAlign] after 200ms", label, {
      after: vv?.currentTime,
      paused: vv?.paused,
      rate: vv?.playbackRate,
    });
  }, 200);
}


/** ======== Keep playing after tab switch ======== **/
/** ======== Tab switch resilience (single owner) ======== **/
useEffect(() => {
  if (!session?.code) return;

  let resumeTimer = null;

  const onVis = () => {
    const hidden = document.visibilityState !== "visible";
    tabHiddenRef.current = hidden;

    if (hidden) return;

    // Visible again: 1 gecontroleerde re-anchor (niet dubbel via focus)
    if (resumeTimer) clearTimeout(resumeTimer);
    resumeTimer = setTimeout(() => {
  // ✅ eerst even PI guard zetten, dan align
  resumeGuardUntilRef.current = Date.now() + 900;
  forceAlign("vis");
}, 900);

  };

  document.addEventListener("visibilitychange", onVis);

  return () => {
    document.removeEventListener("visibilitychange", onVis);
    if (resumeTimer) clearTimeout(resumeTimer);
  };
}, [session?.code, isHost, serverOffsetMs]);



/** ======== Sync metrics → server (guests only) ======== **/
useEffect(() => {
  if (!session?.code) return;
  if (isHost) return; // alleen guests sturen drift metrics

  const id = setInterval(() => {
    const v = videoRef.current;
    if (!v) return;

    const driftArr = driftSamplesRef.current || [];
    const avgS = driftArr.length
      ? driftArr.reduce((a, b) => a + b, 0) / driftArr.length
      : 0;
    const p95S = calcP95Seconds(driftArr);

    const lm = liveMetricsRef.current || {};
    const payload = {
      code: session.code,
      sourceMode: source?.mode || null,

      driftAvgMs: Math.round(avgS * 1000),
      driftP95Ms: Math.round(p95S * 1000),

      hardSeeks: hardSeeksRef.current || 0,
      rate: Number(v.playbackRate || 1),

      bufferSec: Number(bufferHeadroomSec(v).toFixed(3)),

      liveEdge: typeof lm.liveEdge === "number" ? Number(lm.liveEdge.toFixed(3)) : null,
      bufStart: typeof lm.bufStart === "number" ? Number(lm.bufStart.toFixed(3)) : null,
      bufEnd: typeof lm.bufEnd === "number" ? Number(lm.bufEnd.toFixed(3)) : null,

      serverOffsetMs: Math.round(serverOffsetMs || 0),
      bestRttMs: bestRttMs != null ? Math.round(bestRttMs) : null,

      ts: Date.now(),
    };

    socket.emit("sync:metrics", payload, () => {});
  }, 2000);

  return () => clearInterval(id);
}, [session?.code, isHost, source?.mode, serverOffsetMs, bestRttMs]);



  /** ======== Closed-loop PI controller (guests only) ======== **/
  useEffect(() => {
    if (isHost) return;
    const v = videoRef.current;
    if (!v) return;

    const tick = () => {
            if (suppressRef.current) return; // ✅ geen PI terwijl we alignen/seeken
      if (Date.now() < (resumeGuardUntilRef.current || 0)) return; // ✅ even rust na resume

// Always-play mode: blijf progress sturen zolang video echt speelt
if (v.paused || v.ended) return;

      const s = mediaStateRef.current;
      if (!s || !s.playing) {
        // decay naar 1.0
        if (v.playbackRate !== 1)
          v.playbackRate +=
            (1 - v.playbackRate) *
            Math.min(1, profile.LOOP_TICK_MS / profile.RATE_DECAY_MS);
        return;
      }

      // target tov server-epoch
      const serverNow = Date.now() + serverOffsetMs;

      const elapsed = Math.max(0, (serverNow - s.serverEpoch) / 1000);
      const target = s.mediaTime + elapsed;

      
// ✅ Fase 2.E — Live/HLS guard
// ✅ Live/HLS guard (robust): vertrouw op VideoPlayer.safeSeek() voor clamping
// App.js moet NIET op liveEdge uit liveMetrics leunen (kan stale zijn bij tab-throttling)
if (source?.mode === "hls") {
  const { bufStart, now } = liveMetricsRef.current || {};
  const fresh = typeof now === "number" && (Date.now() - now) < 2500;

  // Alleen buffer-start check als metrics recent zijn
  if (fresh && typeof bufStart === "number" && Number.isFinite(bufStart) && target < bufStart - 0.25) {
    safeSeek(target); // VideoPlayer clamped zelf naar liveEdge/buffer
    return;
  }
}



      // fout
      const err = target - v.currentTime; // + = achterlopen
      // telemetry sample
      const samples = driftSamplesRef.current;
      samples.push(err);
      if (samples.length > TELEMETRY_WINDOW) samples.shift();

      // filter
      const alpha = profile.JITTER_FILTER;
      filtErrRef.current = (1 - alpha) * filtErrRef.current + alpha * err;

      // harde correctie?
      const nowMs = Date.now();
      if (
        Math.abs(err) > profile.SOFT_SEEK_S &&
        nowMs - lastHardSeekAtRef.current > profile.SEEK_COOLDOWN_MS
      ) {
        try {
safeSeek(target);
        } catch {}
        lastHardSeekAtRef.current = nowMs;
        integErrRef.current = 0;
        filtErrRef.current = 0;
        hardSeeksRef.current += 1;
        v.playbackRate = 1;
        return;
      }

      // PI-regeling (met integrator clamp)
      integErrRef.current +=
        filtErrRef.current * (profile.LOOP_TICK_MS / 1000);
      const I_MAX = 2.0;
      if (integErrRef.current > I_MAX) integErrRef.current = I_MAX;
      if (integErrRef.current < -I_MAX) integErrRef.current = -I_MAX;

      let delta =
        profile.KP * filtErrRef.current + profile.KI * integErrRef.current;

      // adaptive max delta: boost bij recente fragments/join/seek
      const boostActive = nowMs < lastFragBoostUntilRef.current;
      const maxDelta = boostActive
        ? profile.MAX_RATE_DELTA_BOOST
        : profile.MAX_RATE_DELTA_BASE;

      // clamp & apply
      delta = Math.max(-maxDelta, Math.min(maxDelta, delta));
      const targetRate = 1 + delta;

     


      if (Math.abs(filtErrRef.current) < 0.02) {
        // langzaam terug naar 1
        v.playbackRate +=
          (1 - v.playbackRate) *
          Math.min(1, profile.LOOP_TICK_MS / profile.RATE_DECAY_MS);
      } else {
        // niet versnellen als buffer krap is
        const bh = bufferHealthSeconds(v);
        if (bh < profile.MIN_BUFFER_OK_S && targetRate > v.playbackRate) {
          v.playbackRate +=
            (1 - v.playbackRate) *
            Math.min(1, profile.LOOP_TICK_MS / profile.RATE_DECAY_MS);
        } else {
          v.playbackRate = targetRate;
        }
      }
    };

    loopTimerRef.current = setInterval(tick, profile.LOOP_TICK_MS);
    return () => clearInterval(loopTimerRef.current);
  }, [isHost, serverOffsetMs, profile]);

  /** ======== Shortcuts (F: fullscreen, D: HUD, V: video-only) ======== **/
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "f" || e.key === "F") toggleFullscreen();
      if (e.key === "d" || e.key === "D") setShowHud((x) => !x);
      if (e.key === "v" || e.key === "V") setVideoOnly((v) => !v);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  /** ======== Overlay auto-hide ======== **/
  useEffect(() => {
    if (!overlayVisible) return;
    if (overlayTimeoutRef.current) clearTimeout(overlayTimeoutRef.current);
    overlayTimeoutRef.current = setTimeout(
      () => setOverlayVisible(false),
      2200
    );
    return () => {
      if (overlayTimeoutRef.current) clearTimeout(overlayTimeoutRef.current);
    };
  }, [overlayVisible]);


  // === Click outside: Topbar "Vrienden uitnodigen" panel ===
  useEffect(() => {
    if (!showInvitePanel) return;

    function handleClickOutside(event) {
      const box = invitePanelWrapperRef.current;
      if (box && box.contains(event.target)) {
        // klik was binnen de knop/panel → niks doen
        return;
      }
      setShowInvitePanel(false);
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [showInvitePanel]);


    // === Click outside: BondMiniInvitePanel (B-icoon in videoplayer) ===
  useEffect(() => {
    if (!showMiniInvite) return;

    function handleClickOutside(event) {
      const box = miniInviteWrapperRef.current;
      if (box && box.contains(event.target)) {
        return; // klik binnen de wrapper → niks doen
      }
      setShowMiniInvite(false);
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [showMiniInvite]);


  /** ======== Layout manager ======== **/
    const layout = useMemo(() => {
    const vw = window.innerWidth || 1920;
    const narrow = vw < 900;

    if (!session)
      return {
        mode: "menu",
        rightColPct: 0,
        bottomVh: 0,
        tileAspect: "1 / 1",
        overflow: "hidden",
      };

    // peerCount = jij + vrienden (op basis van socket-ids)
    const remoteCount = peerCount > 0 ? peerCount - 1 : 0;

    // Hoeveel “tegels” gebruikt de layout?
    // - self view aan → gewoon peerCount
    // - self view uit:
    //     - bij 1 remote: doe alsof er 2 tiles zijn (remote + placeholder)
    //     - anders: aantal remotes
    let visibleTileCount;
    if (showSelfView) {
      visibleTileCount = peerCount;
    } else {
      if (remoteCount === 1) visibleTileCount = 2;
      else visibleTileCount = remoteCount;
    }

    if (!narrow && visibleTileCount >= 1 && visibleTileCount <= 4) {
      const rightColPct = Math.min(
        24,
        Math.max(18, 18 + 2 * (visibleTileCount - 1))
      );
      return {
        mode: "right",
        rightColPct,
        bottomVh: 0,
        tileAspect: "4 / 5",
        overflow: "hidden",
      };
    }

    const base =
      visibleTileCount <= 4
        ? 20 + 2 * (visibleTileCount - 1)
        : 16 + 1 * visibleTileCount;
    const bottomVh = Math.min(28, Math.max(18, base));
    const overflow = visibleTileCount > 10 ? "auto" : "hidden";

    return {
      mode: "bottom",
      rightColPct: 0,
      bottomVh,
      tileAspect: "4 / 3",
      overflow,
    };
  }, [peerCount, session, showSelfView]);

    // Aantal andere deelnemers (dus zonder jezelf)
  const otherCount = Math.max(0, peerCount - 1);


  /** ======== Styles ======== **/
const shellStyle = {
  fontFamily: "system-ui, sans-serif",
  height: "100vh",        // vaste viewport-hoogte
  display: "flex",
  flexDirection: "column",
  background: "#020617",
  color: "#e5e7eb",
  overflow: "hidden",     // geen page-scroll, geen scrollbar
};



  const iconBtn = {
    background: "rgba(0,0,0,0.55)",
    border: "none",
    color: "#fff",
    borderRadius: 8,
    padding: 8,
    cursor: "pointer",
  };
  const icon = { width: 18, height: 18, display: "block" };
  const videoElStyle = {
    width: "100%",
    height: "100%",
    objectFit: zoomFill ? "cover" : "contain",
    pointerEvents: isHost ? "auto" : "none",
    background: "#000",
  };

  /** ======== Helpers ======== **/
  function bufferHealthSeconds(v) {
    if (!v) return 0;
    const t = v.currentTime;
    for (let i = 0; i < v.buffered.length; i++) {
      const start = v.buffered.start(i);
      const end = v.buffered.end(i);
      if (t >= start && t <= end) return end - t;
    }
    return 0;
  }
 


function stopLocalMedia() {
  // pak stream uit ref of uit state (voor de zekerheid)
  const s = localStreamRef.current || localStream;
  if (s) {
    try {
      s.getTracks().forEach((t) => {
        try {
          t.stop();
        } catch {}
      });
    } catch (e) {
      console.warn("[Bond] stopLocalMedia error", e);
    }
  }

  // refs & state resetten
  localStreamRef.current = null;
  setLocalStream(null);

  // PeerManager vertellen dat er geen local stream meer is
  if (peerMgrRef.current && typeof peerMgrRef.current.setLocalStream === "function") {
    try {
      peerMgrRef.current.setLocalStream(null);
    } catch (e) {
      console.warn("[Bond] peerMgr setLocalStream(null) error", e);
    }
  }
}


    /** ======== WebRTC PeerManager lifecycle (hardened) ======== **/
useEffect(() => {
  // Alleen starten als we écht in een sessie-view zitten
  if (!session?.code) return;

  let cancelled = false;

  // Gen bump: alles dat nog loopt van een vorige sessie moet stoppen
  const myGen = ++rtcStartGenRef.current;
  rtcStartingRef.current = true;

  const mgr = new PeerManager({
    // ✅ Belangrijk: gebruik ref (altijd up-to-date), state is soms te laat
    getLocalStream: () => localStreamRef.current || localStream || null,

    onPeerStream: (peerId, stream) => {
      peerStreamsRef.current.set(peerId, stream);
      forceRerender((x) => x + 1);
    },

    onPeerRemove: (peerId) => {
      // 🔥 ICE recovery: alles resetten
      if (peerId === "*") {
        peerStreamsRef.current.clear();
        forceRerender((x) => x + 1);
        return;
      }

      // Normale peer leave
      peerStreamsRef.current.delete(peerId);
      forceRerender((x) => x + 1);
    },

    // ⚠️ GEEN eigen iceServers hier; PeerManager gebruikt rtc.js defaults
  });

  peerMgrRef.current = mgr;
  mgr.wireSocketHandlers();

  // Kleine helper: wacht op socket connect (scheelt “join soms faalt”)
  const waitForSocketConnected = () =>
    new Promise((resolve) => {
      if (!socket) return resolve();
      if (socket.connected) return resolve();

      const onConnect = () => {
        socket.off?.("connect", onConnect);
        resolve();
      };

      socket.on?.("connect", onConnect);

      // safety timeout: blokkeer niet eindeloos
      setTimeout(() => {
        socket.off?.("connect", onConnect);
        resolve();
      }, 2500);
    });

  (async () => {
    try {
      await waitForSocketConnected();

      // Stop als er intussen een nieuwe sessie gestart is of we unmounten
      if (cancelled || rtcStartGenRef.current !== myGen) return;

      await mgr.joinRoom();

      if (cancelled || rtcStartGenRef.current !== myGen) return;

      // ✅ Local stream push (als die er al is)
      const ls = localStreamRef.current || localStream || null;
      if (ls && typeof mgr.setLocalStream === "function") {
        await mgr.setLocalStream(ls);
      }
    } catch (e) {
      if (!cancelled) console.warn("[Bond] rtc joinRoom failed", e);
    } finally {
      if (!cancelled && rtcStartGenRef.current === myGen) {
        rtcStartingRef.current = false;
      }
    }
  })();

  return () => {
    cancelled = true;

    try {
      mgr.destroy?.();
    } catch (e) {
      console.warn("[Bond] PeerManager destroy error", e);
    } finally {
      if (peerMgrRef.current === mgr) {
        peerMgrRef.current = null;
      }
      peerStreamsRef.current.clear();
      forceRerender((x) => x + 1);
    }
  };

  // Belangrijk: dependency op session.code ipv hele session object
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [session?.code]);




  /** ======== Mijn Bond-pakketten / providers laden ======== **/
  useEffect(() => {
    if (!token) {
      setMyProviders([]);
      setMyMaxPackageLevel(0);
      return;
    }

    (async () => {
      try {
        const data = await apiGetMyProviders(token);
        console.log("[Bond] providers response =", data);

        const rawIds = (data.providers || []).map((p) =>
          String(p.providerId || "").toLowerCase()
        );

        const uniqueIds = [...new Set(rawIds)];
        console.log("[Bond] raw providerIds =", uniqueIds);

        // Map alle mogelijke provider-id's naar een 'pakket-level'
        // 0 = free, 1 = sports / ESPN / Viaplay, 2 = all_access / Ziggo
        const LEVELS = {
          free: 0,
          sports: 1,
          all_access: 2,

          // oude IDs uit eerdere versies:
          espn: 1,
          viaplay: 1,
          ziggo: 2,
        };

        const levels = uniqueIds.map((id) => LEVELS[id] ?? 0);
        const maxLevel = levels.length ? Math.max(...levels) : 0;

        console.log(
          "[Bond] myProviders =",
          uniqueIds,
          "→ maxPackageLevel =",
          maxLevel
        );

        setMyProviders(uniqueIds);
        setMyMaxPackageLevel(maxLevel);
      } catch (e) {
        console.warn("[Bond] kon abonnementen niet laden", e);
        setMyProviders([]);
        setMyMaxPackageLevel(0);
      }
    })();
  }, [token]);



  // ======== Friends voor sessie-invites laden ========
  useEffect(() => {
    if (!session || !token) return;

    let cancelled = false;

    (async () => {
      try {
        setSessionFriendsLoading(true);
        const res = await apiGetFriends(token);
        if (cancelled) return;
        setSessionFriends(res?.friends || []);
      } catch (e) {
        console.warn("[Bond] kon session friends niet laden", e);
      } finally {
        if (!cancelled) setSessionFriendsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [session, token]);


  /** ======== Push lokale tracks zodra localStream wijzigt ======== **/
 /** ======== Push lokale tracks zodra localStream wijzigt ======== **/
useEffect(() => {
  const mgr = peerMgrRef.current;
  const s = localStreamRef.current || localStream || null;
  if (!mgr || !s) return;

  (async () => {
    try {
      if (typeof mgr.setLocalStream === "function") {
        await mgr.setLocalStream(s);
      } else if (typeof mgr.addLocalTracksToAll === "function") {
        await mgr.addLocalTracksToAll();
      }
    } catch (e) {
      console.warn("[Bond] push local tracks failed", e);
    }
  })();
}, [localStream]);



useEffect(() => {
  // Als er geen sessie meer is, zorg 100% dat lokale media uit zijn
  if (!session) {
    stopLocalMedia();
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [session]);


  /** ======== Video-audio effect (koppelt mixer → <video>) ======== **/
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = videoMuted;
    v.volume = videoVolume;
  }, [videoMuted, videoVolume]);

  /** ======== Chat versturen ======== **/
  function handleSendChatMessage(text) {
    if (!session || !text) return;
    socket.emit("chat:send", {
      sessionCode: session.code,
      text,
    });
  }


  /** ======== Leave-logic (sessie verlaten) ======== **/
async function handleLeaveSession() {
  // 1) Server vertellen dat we de sessie verlaten
  await leaveSession();

  // 2) Alle lokale media hard uit
  stopLocalMedia();

  // 3) Lokale session-state opruimen
  setSession(null);
  setSessionState(null);
  setPeerCount(0);
  setIsHost(false);
  setVideoOnly(false);
  setShowSelfView(true); // nieuwe sessie begint weer met self view aan
  setView("friends");    // terug naar vriendenpagina
}

   /** ======== Handlers voor FriendsPage ======== **/
  // Host: vanuit vriendenlijst een sessie starten (nu eerst content kiezen)
  function handleInviteSelectedFriends(friendIds) {
    if (!friendIds || friendIds.length === 0) return;

    // Onthoud dat we met vrienden willen starten
    setPendingSessionPlan({
      type: "invite",
      friendIds: friendIds.map(String),
    });
    setShowContentPicker(true);
  }



 // Gast: accepteert een sessie-invite vanuit FriendsPage
async function handleAcceptSessionInvite(sessionCode) {
  try {

unlockRemoteAudioOnce();


    const res = await joinSessionWithFeedback(sessionCode);
    if (res) {
      setSession(res);
      setIsHost(!!res.isHost);
      setView("session");
    }
  } catch (e) {
    console.warn("[Bond] handleAcceptSessionInvite error", e);
  }
}


  // ======== Invites versturen vanuit de sessie (host + guests) ========
  async function handleInviteFriendsInSession(friendIds) {
    if (!session || !token || !friendIds?.length) return;
    try {
      // Ook hier: robuust sessionCode bepalen uit de huidige session-state
      let sessionCode =
        session.code ||
        session.sessionCode ||
        (typeof session.sessionId === "string" &&
        session.sessionId.startsWith("room:")
          ? session.sessionId.slice("room:".length)
          : session.sessionId);

      if (!sessionCode) {
        console.warn("[Bond] Geen geldige sessionCode in handleInviteFriendsInSession", session);
        return;
      }

      setSessionInviteBusy(true);
      await apiCreateSessionInvites(sessionCode, friendIds, token);
      // Paneel sluiten na succes
      setShowInvitePanel(false);
    } catch (e) {
      console.warn("[Bond] handleInviteFriendsInSession error", e);
    } finally {
      setSessionInviteBusy(false);
    }
  }

   /** ======== Sessie starten: eerst content kiezen ======== **/
  function actuallyStartSession() {
    // Solo Bondsessie: eerst wedstrijd / kanaal kiezen
    setPendingSessionPlan({ type: "solo" });
    setShowContentPicker(true);
  }

    async function startPlannedSessionWithContent(selection) {
    if (!selection || !pendingSessionPlan) return;

unlockRemoteAudioOnce();


    if (!token) {
      console.warn("[Bond] geen token in startPlannedSessionWithContent");
      return;
    }

    const { providerId, assetId, title, assetUrl } = selection;

    try {
      // 1. Sessie aanmaken via socket (createSession)
      const res = await createSession(
  {
    name: title || sessionName || "Bondsessie",
    providerId,
    contentId: assetId,
    contentTitle: title,
  },
  token
);


      if (!res?.ok) {
        console.warn("[Bond] createSession faalde", res);
        return;
      }

      // Code netjes extraheren
      let sessionCode =
        res.code ||
        res.sessionCode ||
        (typeof res.sessionId === "string" &&
          res.sessionId.startsWith("room:")
          ? res.sessionId.slice("room:".length)
          : res.sessionId);

      if (!sessionCode) {
        console.warn(
          "[Bond] geen geldige sessionCode na createSession",
          res
        );
        return;
      }

      const fullSession = { ...res, code: sessionCode };
      setSession(fullSession);
      setIsHost(true);
      setView("session");

      // 2. Videobron instellen op basis van gekozen demo
      const src = {
        mode: "hls",
        url: assetUrl,
        providerId,
        label: title,
      };
      setSource(src);

const ack = await setMediaSource(src, token);
if (!ack?.ok) {
  console.warn("[Bond] media:source geweigerd", ack);
  window.alert("Kon deze content niet starten.");
  // optioneel: rollback naar default free stream
}


      // 3. Bij "invite"-flow: direct sessie-invites sturen
      if (
        pendingSessionPlan.type === "invite" &&
        pendingSessionPlan.friendIds &&
        pendingSessionPlan.friendIds.length
      ) {
        await apiCreateSessionInvites(
          sessionCode,
          pendingSessionPlan.friendIds,
          token
        );
      }
    } catch (e) {
      console.warn("[Bond] startPlannedSessionWithContent error", e);
    } finally {
      setShowContentPicker(false);
      setPendingSessionPlan(null);
    }
  }


  function handleLogout() {
    // netjes via AuthContext (haalt zelf tokens uit storage)
    logout();
    // terug naar landing view
    setView("landing");
  }



  /** ======== Start-flow vanaf landing: naar vriendenpagina ======== **/
  async function handleStartSession() {
    if (initializing) return;

    if (!currentUser) {
      // eerst inloggen, daarna naar friends-pagina
      setPendingAction("open-friends");
      setAuthMode("login");
      setShowAuthModal(true);
      return;
    }

    // al ingelogd → direct naar friends-pagina
    setView("friends");
  }


  /** ======== Render ======== **/
  const pathname =
    typeof window !== "undefined" ? window.location.pathname : "/";

  // Speciale case: magic link callback
  if (pathname === "/auth/callback") {
    return (
      <div style={shellStyle}>
        <AuthCallbackPage />
      </div>
    );
  }

   // Normale Bond app
  return (
    <div style={shellStyle}>




      {/* TopBar alleen in-session */}
      {view === "session" && session && (
        <TopBar
          centerContent={
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 24,
              }}
            >
             {/* Invite-knop + panel in de bar */}
<div
  ref={invitePanelWrapperRef}
  style={{ position: "relative", display: "inline-block" }}
>
  <button
    style={{
      padding: "10px 24px",
      borderRadius: 999,
      border: "none",
      background:
        "linear-gradient(135deg, #4f46e5 0%, #6366f1 40%, #ec4899 100%)",
      color: "#fff",
      fontSize: 14,
      fontWeight: 500,
      cursor: "pointer",
      boxShadow: "0 10px 30px rgba(15, 23, 42, 0.85)",
      whiteSpace: "nowrap",
    }}
    onClick={() => setShowInvitePanel((v) => !v)}
  >
    Vrienden uitnodigen
  </button>

  {showInvitePanel && (
    <SessionInvitePanel
      friends={sessionFriends}
      busy={sessionInviteBusy || sessionFriendsLoading}
      onInvite={handleInviteFriendsInSession}
    />
  )}
</div>


              {/* Leave-knop */}
              <button
                style={{
                  padding: "10px 24px",
                  borderRadius: 999,
                  border: "none",
                  background:
                    "linear-gradient(135deg, #f97373 0%, #ef4444 45%, #b91c1c 100%)",
                  color: "#fff",
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: "pointer",
                  boxShadow: "0 10px 30px rgba(127, 29, 29, 0.9)",
                }}
                onClick={handleLeaveSession}
              >
                Leave
              </button>
            </div>
          }
        />
      )}

      {/* Auth-modal, over alles heen */}
      {showAuthModal && (
        <AuthModal
          mode={authMode}
          onSwitchMode={setAuthMode}
          onClose={() => {
            setShowAuthModal(false);
            setPendingAction(null);
          }}
        />
      )}

      {/* Homescreen als je niet in een sessie zit & view=landing */}
      {view === "landing" && !session && (
        <div className="bond-landing">
          <div className="bond-landing-logo-wrap">
            <span className="bond-landing-logo-text">BOND</span>
          </div>

          <div
            style={{
              marginTop: 12,
              height: 52,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "opacity 300ms ease",
              opacity: showLandingButton ? 1 : 0,
            }}
          >
            {showLandingButton && (
              <button
                style={{
                  padding: "12px 28px",
                  borderRadius: 999,
                  border: "none",
                  background:
                    "linear-gradient(135deg, #4f46e5 0%, #6366f1 40%, #ec4899 100%)",
                  color: "#fff",
                  fontSize: 16,
                  fontWeight: 500,
                  cursor: "pointer",
                  boxShadow: "0 14px 40px rgba(15, 23, 42, 0.85)",
                }}
                onClick={handleStartSession}
              >
                Bondsessie starten
              </button>
            )}
          </div>
        </div>
      )}

      {/* Vriendenpagina (Friends view) */}
      {view === "friends" && currentUser && token && (
        <FriendsPage
          currentUser={currentUser}
          token={token}
          onInviteFriends={handleInviteSelectedFriends}
          onAcceptSessionInvite={handleAcceptSessionInvite}
          onStartSoloSession={actuallyStartSession}
          onLogout={handleLogout}
          onOpenProviders={() => setView("providers")}
        />
      )}

      {/* Providerspagina (Mijn abonnementen) */}
      {view === "providers" && currentUser && (
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            padding: 16,
            maxWidth: 900,
            margin: "0 auto",
            width: "100%",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 12,
            }}
          >
            <button
              onClick={() => setView("friends")}
              style={{
                border: "none",
                background: "transparent",
                color: "#9ca3af",
                fontSize: 13,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <span style={{ fontSize: 16 }}>←</span>
              <span>Terug naar vrienden</span>
            </button>
          </div>

          {/* De eigenlijke providers-pagina */}
          <ProvidersPage currentUser={currentUser} token={token} />
        </div>
      )}

         {/* In-session (Session view) */}
      {view === "session" && session && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
padding: "24px 16px 56px",
            gap: 0,
            minHeight: 0,
            overflow: "hidden", // geen page-scroll in de sessie
          }}
        >
          {/* Stage */}
          <div
            ref={stageRef}
            style={
              layout.mode === "right"
                ? {
                    position: "relative",
                    display: "flex",
                    flexDirection: "row",
                    flex: 1,
                    gap: 0,
                    minHeight: 0,
                    overflow: "hidden",
                    alignItems: "stretch",
                  }
                : {
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    gap: 0,
                    minHeight: 0,
                    overflow: "hidden",
                    alignItems: "stretch",
                  }
            }
          >
           {/* Join-toast binnen de stage, bovenin gecentreerd (Bond-stijl) */}
{joinToast && (
  <div className="bond-join-toast">
    <div className="bond-join-toast-pill">
      <div className="bond-join-toast-glow" />

      <div className="bond-join-toast-avatar">
        <span>
          {(joinToast.name || "?").charAt(0).toUpperCase()}
        </span>
      </div>

      <div className="bond-join-toast-body">
        <span className="bond-join-toast-label">Nieuw in je sessie</span>
        <span className="bond-join-toast-main">
          <strong>{joinToast.name}</strong> is je Bondsessie gejoined.
        </span>
      </div>
    </div>
  </div>
)}



            {/* Chat overlay linksboven, over video / tiles heen */}
            {showChat && (
              <div
                style={{
                  position: "absolute",
                  left: 12,
                  top: 12,
                  bottom: 12,
                  width: "min(340px, 32vw)",
                  zIndex: 30,
                  pointerEvents: "auto",
                }}
              >
                <ChatPanel
                  messages={chatMessages}
                  onSend={handleSendChatMessage}
                  onClose={() => setShowChat(false)}
                />
              </div>
            )}

          {/* Video area */}
<div
  style={
    layout.mode === "right"
      ? {
          flex: "1 1 auto",
          position: "relative",
          display: "flex",
          alignItems: "stretch",
          justifyContent: "center",
          background: "#000",
          overflow: "hidden",
          minHeight: 0,
          width: "100%",
        }
      : {
          flex: 1,
          position: "relative",
          display: "flex",
          alignItems: "stretch",
          justifyContent: "center",
          background: "#000",
          overflow: "hidden",
          minHeight: 0,
          width: "100%",
        }
  }
  onMouseMove={() => setOverlayVisible(true)}
  onMouseEnter={() => setOverlayVisible(true)}
  onClick={() => {
  setOverlayVisible(true);
unlockRemoteAudioOnce();

}}

>

            
              {/* Audio mixer linksonder */}
              <div
                style={{
                  position: "absolute",
                  left: 12,
                  bottom: 12,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "6px 10px",
                  background: "rgba(0,0,0,0.55)",
                  borderRadius: 8,
                  zIndex: 2,
                  opacity: overlayVisible ? 1 : 0,
                  transition: "opacity 200ms ease",
                  pointerEvents: overlayVisible ? "auto" : "none",
                }}
              >
                <button
                  onClick={() => setVideoMuted((prev) => !prev)}
                  title={videoMuted ? "Geluid aan" : "Geluid uit"}
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 6,
                    border: 0,
                    background: "rgba(255,255,255,0.15)",
                    color: "#fff",
                    cursor: "pointer",
                    display: "grid",
                    placeItems: "center",
                    fontSize: 16,
                  }}
                >
                  {videoMuted ? "🔇" : "🔊"}
                </button>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={videoVolume}
                  onChange={(e) => {
                    const val = Number(e.target.value);
                    setVideoVolume(val);
                    if (val > 0) setVideoMuted(false);
                  }}
                  title="Volume"
                  style={{ width: 120, cursor: "pointer" }}
                />
              </div>

              {/* Video overlay controls — één rij, rechtsonder, auto-hide */}
              <div
                style={{
                  position: "absolute",
                  right: 12,
                  bottom: 12,
                  display: "flex",
                  gap: 8,
                  zIndex: 2,
                  opacity: overlayVisible ? 1 : 0,
                  transition: "opacity 200ms ease",
                  pointerEvents: overlayVisible ? "auto" : "none",
                }}
              >
               {/* Bond mini invite icon + panel */}
<div ref={miniInviteWrapperRef} style={{ position: "relative" }}>
  <button
    style={iconBtn}
    onClick={() => setShowMiniInvite((v) => !v)}
    title="Bond - vrienden uitnodigen"
  >
    <img
      src="/images/favicon.ico"
      alt="Bond"
      style={{
        width: 18,
        height: 18,
        display: "block",
        borderRadius: 4,
      }}
    />
  </button>

  {showMiniInvite && (
    <BondMiniInvitePanel
      friends={sessionFriends}
      busy={sessionInviteBusy || sessionFriendsLoading}
      onInvite={async (friendIds) => {
        await handleInviteFriendsInSession(friendIds);
      }}
      onClose={() => setShowMiniInvite(false)}
    />
  )}
</div>


                {/* Zoom (fit / fill) */}
                <button
                  style={iconBtn}
                  onClick={() => setZoomFill((z) => !z)}
                  title={zoomFill ? "Beeld vullen" : "Beeld passen"}
                >
                  <svg viewBox="0 0 24 24" style={icon} aria-hidden="true">
                    {zoomFill ? (
                      <path
                        fill="currentColor"
                        d="M5 5h14v14H5V5zm3 3v8h8V8H8z"
                      />
                    ) : (
                      <path
                        fill="currentColor"
                        d="M5 5h5v2H7v3H5V5zm9 0h5v5h-2V7h-3V5zm-9 9h2v3h3v2H5v-5zm13 3v-3h2v5h-5v-2h3z"
                      />
                    )}
                  </svg>
                </button>

                {/* Fullscreen (met cams) */}
          <button
  style={iconBtn}
  onClick={toggleFullscreen}
  title={isFs ? "Fullscreen uit" : "Fullscreen aan"}
>
  <svg
    viewBox="0 0 24 24"
    style={icon}
    aria-hidden="true"
  >
    {isFs ? (
      // ============================
      // 🔽 JE ZIT IN FULLSCREEN
      // pijlen naar binnen (oude versie)
      // ============================
      <g
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {/* pijltje linksboven → midden */}
        <path d="M7 7 L11 11" />
        <path d="M11 11 L11 8" />
        <path d="M11 11 L8 11" />

        {/* pijltje rechtsonder → midden */}
        <path d="M17 17 L13 13" />
        <path d="M13 13 L13 16" />
        <path d="M13 13 L16 13" />
      </g>
    ) : (
      // ============================
      // 🔼 NIET FULLSCREEN
      // pijlen naar buiten (grote versie)
      // ============================
      <g
        fill="none"
        stroke="currentColor"
        strokeWidth="3.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {/* pijltje midden → linksboven */}
        <path d="M12 12 L5 5" />
        <path d="M5 5 L5 9" />
        <path d="M5 5 L9 5" />

        {/* pijltje midden → rechtsonder */}
        <path d="M12 12 L19 19" />
        <path d="M19 19 L19 15" />
        <path d="M19 19 L15 19" />
      </g>
    )}
  </svg>
</button>


                {/* Self-view toggle */}
                <button
                  style={{
                    ...iconBtn,
                    opacity: peerCount === 0 ? 0.4 : 1,
                    cursor: peerCount === 0 ? "default" : "pointer",
                  }}
                  disabled={peerCount === 0}
                  onClick={() => {
                    if (peerCount === 0) return;
                    setShowSelfView((v) => !v);
                  }}
                  title={
                    peerCount === 0
                      ? "Wacht op een andere deelnemer…"
                      : showSelfView
                      ? "Verberg mijn camtile"
                      : "Toon mijn camtile"
                  }
                >
                  <svg viewBox="0 0 24 24" style={icon} aria-hidden="true">
                    <path
                      fill="currentColor"
                      d="M3 5h18v12H3V5zm2 2v8h14V7H5z"
                    />
                    <circle cx="12" cy="11" r="2" fill="currentColor" />
                    <path
                      fill="currentColor"
                      d="M9.5 15c.6-1.2 1.6-2 2.5-2s1.9.8 2.5 2H9.5z"
                    />
                  </svg>
                </button>

                {/* Fullscreen zonder cams (tiles verborgen, audio blijft) */}
                <button
                  style={iconBtn}
                  onClick={() => setVideoOnly((v) => !v)}
                  title={
                    videoOnly ? "Tiles weer tonen" : "Fullscreen (geen cam tiles)"
                  }
                >
                  <svg viewBox="0 0 24 24" style={icon} aria-hidden="true">
                    <path
                      fill="currentColor"
                      d="M4 4h16v12H4V4zm2 2v8h12V6H6z"
                    />
                    <rect
                      x="16"
                      y="14"
                      width="4"
                      height="4"
                      fill="currentColor"
                    />
                  </svg>
                </button>

                {/* HUD toggle (debug) */}
                <button
                  style={iconBtn}
                  onClick={() => setShowHud((x) => !x)}
                  title="Debug HUD (D)"
                >
                  <svg viewBox="0 0 24 24" style={icon} aria-hidden="true">
                    <path
                      fill="currentColor"
                      d="M5 19h2V10H5v9zm6 0h2V5h-2v14zm6 0h2v-8h-2v8z"
                    />
                  </svg>
                </button>
              </div>

              <VideoPlayer
                ref={videoRef}
                source={source}
                serverOffsetMs={serverOffsetMs}
                profile={profile}
hlsPreset={
  source?.mode === "hls"
    ? (new URLSearchParams(window.location.search).get("ll") === "1" ? "ll" : "smooth")
    : null
}

                mediaStateRef={mediaStateRef}
                needInitialAlignRef={needInitialAlignRef}
                didInitialAlignRef={didInitialAlignRef}
                lastFragBoostUntilRef={lastFragBoostUntilRef}
                hardSeeksRef={hardSeeksRef}
                  onLiveMetrics={(m) => { liveMetricsRef.current = m || liveMetricsRef.current; }}
  onSafeSeekRef={(fn) => { safeSeekFnRef.current = fn; }}

                style={videoElStyle}
                onDoubleClick={toggleFullscreen}
                objectFit={zoomFill ? "cover" : "contain"}
              />

              {showHud && (
                <Hud
                  videoRef={videoRef}
                  driftSamplesRef={driftSamplesRef}
                  hardSeeksRef={hardSeeksRef}
                  serverOffsetMs={serverOffsetMs}
                  liveMetricsRef={liveMetricsRef}
bestRttMs={bestRttMs}

                />
              )}
            </div>


{/* Alle cameratiles via ParticipantsLayout */}
<ParticipantsLayout
  mode={layout.mode}
  layout={layout}
  peerStreams={peerStreamsRef.current}
  soundEnabled={soundEnabled}
  showTiles={!videoOnly}
  showSelfView={showSelfView}
    expectedRemoteCount={Math.max(0, (sessionState?.peerCount || 1) - 1)}

  onLocalStreamChange={(s) => {
    localStreamRef.current = s;
    setLocalStream(s);

    console.log("[App] local stream tracks:", {
  a: (s?.getAudioTracks?.() || []).map(t => ({ id: t.id, enabled: t.enabled, state: t.readyState })),
  v: (s?.getVideoTracks?.() || []).map(t => ({ id: t.id, state: t.readyState })),
});


    const mgr = peerMgrRef.current;
    if (!mgr) return;

    if (typeof mgr.setLocalStream === "function") {
      mgr.setLocalStream(s);
    } else if (typeof mgr.addLocalTracksToAll === "function") {
      mgr.addLocalTracksToAll();
    }
  }}

  onLocalMicChange={(micOn) => {
  const mgr = peerMgrRef.current;
  if (!mgr) return;

  // 1) Toggle mic (track + producer pause/resume)
  if (typeof mgr.setMicEnabled === "function") {
    mgr.setMicEnabled(micOn);
  }

  // 2) ✅ Bond-proof: als mic weer AAN gaat, force een setLocalStream
  // zodat audio-producer nooit kan ontbreken (timing/perms edge cases).
  if (micOn) {
    const s = localStreamRef.current || localStream || null;
    if (s && typeof mgr.setLocalStream === "function") {
      try {
        mgr.setLocalStream(s);
      } catch {}
    }
  }
}}


  onToggleChat={() => setShowChat((v) => !v)}
   // ✅ voeg toe:
  unreadCount={chatUnread}
  forceShowUiKey={chatPeekNonce}
/>
</div>
</div>
)}



      {/* Content picker overlay */}
      {showContentPicker && pendingSessionPlan && (
        <ContentPickerPanel
          catalog={PROVIDER_CATALOG}
          entitlements={entitlements}
          maxPackageLevel={myMaxPackageLevel}
          myProviders={myProviders}
          onCancel={() => {
            setShowContentPicker(false);
            setPendingSessionPlan(null);
          }}
          onSelect={startPlannedSessionWithContent}
        />
      )}
      <KickToastPortal text={kickToast?.text} />

    </div>
  );
}


function KickToastPortal({ text }) {
  if (!text) return null;
  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      style={{
        position: "fixed",
        top: 18,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 2147483647,
        padding: "12px 16px",
        borderRadius: 14,
        background: "rgba(2,6,23,0.92)",
        border: "1px solid rgba(148,163,184,0.35)",
        boxShadow: "0 22px 60px rgba(0,0,0,0.65)",
        color: "#e5e7eb",
        maxWidth: 520,
        width: "calc(100% - 32px)",
        pointerEvents: "none",
      }}
    >
      <div style={{ fontSize: 11, letterSpacing: "0.12em", opacity: 0.75 }}>
        SESSIE BEËINDIGD
      </div>
      <div style={{ marginTop: 4, fontSize: 13, lineHeight: 1.35 }}>
        {text}
      </div>
    </div>,
    document.body
  );
}





  /** ======== HUD component ======== **/
function Hud({ videoRef, driftSamplesRef, hardSeeksRef, serverOffsetMs, liveMetricsRef, bestRttMs }) {
    const v = videoRef.current;

        const startTsRef = React.useRef(Date.now());
    const elapsedMin = (Date.now() - startTsRef.current) / 60000;

    const driftArr = driftSamplesRef.current;
    const avg = driftArr.length
      ? driftArr.reduce((a, b) => a + b, 0) / driftArr.length
      : 0;
    const p95 = driftArr.length
      ? driftArr
          .slice()
          .sort((a, b) => a - b)[
          Math.min(driftArr.length - 1, Math.floor(0.95 * driftArr.length))
        ]
      : 0;
    const bh = v
      ? (() => {
          const t = v.currentTime;
          for (let i = 0; i < v.buffered.length; i++) {
            const start = v.buffered.start(i);
            const end = v.buffered.end(i);
            if (t >= start && t <= end) return end - t;
          }
          return 0;
        })()
      : 0;
    const rate = v?.playbackRate ?? 1;

        const hardSeeksPerMin = elapsedMin > 0.1 ? (hardSeeksRef.current / elapsedMin) : 0;
    const lm = liveMetricsRef?.current || {};


    return (
      <div
        style={{
          position: "absolute",
          left: 10,
          bottom: 10,
          padding: "10px 12px",
          background: "rgba(0,0,0,0.55)",
          color: "#fff",
          borderRadius: 8,
          fontSize: 12,
          lineHeight: 1.5,
          zIndex: 3,
pointerEvents: "auto",
        }}
      >
        <div>
          <b>Bond HUD</b>
        </div>

        <div style={{ marginTop: 6, display: "flex", gap: 8, pointerEvents: "auto" }}>
  <button
    onClick={async () => {
      const nowDrift = driftArr[driftArr.length - 1] || 0;

      const sorted = driftArr.length ? driftArr.slice().sort((a, b) => a - b) : [];
      const p50 = sorted.length ? sorted[Math.min(sorted.length - 1, Math.floor(0.5 * sorted.length))] : 0;
      const p95 = sorted.length ? sorted[Math.min(sorted.length - 1, Math.floor(0.95 * sorted.length))] : 0;
      const max = sorted.length ? sorted[sorted.length - 1] : 0;

      const bhNow = bh;
      const liveEdge = lm.liveEdge;
      const bufStart = lm.bufStart;
      const bufEnd = lm.bufEnd;

      const report = [
        `Bond Sync Scorecard`,
        `-------------------`,
        `Drift now: ${ms(nowDrift)} ms`,
        `Drift p50: ${ms(p50)} ms`,
        `Drift p95: ${ms(p95)} ms`,
        `Drift max: ${ms(max)} ms`,
        `Hard seeks total: ${hardSeeksRef.current}`,
        `Hard seeks/min: ${hardSeeksPerMin.toFixed(2)}`,
        `Rate now: ${(v?.playbackRate ?? 1).toFixed(3)}x`,
        `Buffer now: ${bhNow.toFixed(2)} s`,
        `Live edge: ${typeof liveEdge === "number" ? liveEdge.toFixed(2) : "n/a"} s`,
        `Buffered: ${typeof bufStart === "number" ? bufStart.toFixed(2) : "n/a"} → ${typeof bufEnd === "number" ? bufEnd.toFixed(2) : "n/a"} s`,
        `Srv offset: ${Math.round(serverOffsetMs)} ms`,
        `Best RTT: ${bestRttMs != null ? Math.round(bestRttMs) : "n/a"} ms`,
        `Samples: ${driftArr.length}`,
      ].join("\n");

      try {
        await navigator.clipboard.writeText(report);
        console.log("[Bond HUD] scorecard copied");
      } catch (e) {
        console.log(report);
        window.prompt("Copy scorecard:", report);
      }
    }}
    style={{
      padding: "6px 10px",
      borderRadius: 8,
      border: "1px solid rgba(255,255,255,0.25)",
      background: "rgba(255,255,255,0.12)",
      color: "#fff",
      cursor: "pointer",
      fontSize: 12,
    }}
  >
    Copy scorecard
  </button>
</div>

        <div>Drift now: {ms(driftArr[driftArr.length - 1] || 0)} ms</div>
        <div>Drift avg: {ms(avg)} ms</div>
        <div>Drift p95: {ms(p95)} ms</div>
        <div>Hard seeks: {hardSeeksRef.current}</div>
        <div>Rate: {rate.toFixed(3)}x</div>
        <div>Buffer: {bh.toFixed(2)} s</div>
        <div>Srv offset: {Math.round(serverOffsetMs)} ms</div>
      </div>
    );
  }



/** ======== AuthModal component (Magic link) ======== **/
function AuthModal({ mode, onSwitchMode, onClose }) {
  const { requestMagicLogin } = useAuth();

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);

  const isSignup = mode === "signup";

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setBusy(true);

    try {
      const emailTrim = email.trim();
      const nameTrim = name.trim();

      if (!emailTrim) {
        setError("Vul een e-mailadres in.");
        setBusy(false);
        return;
      }
      if (!nameTrim) {
        setError("Vul een naam (username) in.");
        setBusy(false);
        return;
      }

           await requestMagicLogin(emailTrim, nameTrim);

      // Link is succesvol aangevraagd.
      // Gebruiker blijft op deze modal en logt pas echt in
      // als hij op de link in de e-mail klikt (AuthCallbackPage).
      setSent(true);

    } catch (err) {
      console.error("[AuthModal] requestMagicLogin error", err);
      const code = err?.code || err?.message || "ERROR";

      if (code === "DISPLAY_NAME_REQUIRED") {
        setError("Vul een naam (username) in.");
      } else if (code === "EMAIL_REQUIRED") {
        setError("Vul een e-mailadres in.");
      } else if (code === "DISPLAY_NAME_ALREADY_IN_USE") {
        setError("Deze username is al in gebruik. Kies een andere naam.");
      } else {
        setError("Er ging iets mis bij het sturen van je link.");
      }
    } finally {
      setBusy(false);
    }
  }

  const title = isSignup ? "Maak je Bond-account" : "Log in bij Bond";
  const subtitle = isSignup
    ? "Kies je username en ontvang een verificatie-link per e-mail."
    : "Vul je gegevens in en ontvang een login-link per e-mail.";

  const buttonLabel = isSignup ? "Stuur verificatie-link" : "Stuur login-link";

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,23,42,0.82)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <div
        style={{
          background: "#020617",
          borderRadius: 24,
          padding: "24px 26px 22px",
          width: "100%",
          maxWidth: 420,
          boxShadow: "0 24px 80px rgba(0,0,0,0.9)",
          border: "1px solid rgba(148,163,184,0.25)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header + close */}
        <div
          style={{
            marginBottom: 16,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <div
              style={{
                fontSize: 16,
                fontWeight: 700,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
              }}
            >
              BOND
            </div>
            <div
              style={{
                marginTop: 4,
                fontSize: 13,
                color: "#9ca3af",
              }}
            >
              {subtitle}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              border: "none",
              borderRadius: 999,
              width: 28,
              height: 28,
              display: "grid",
              placeItems: "center",
              background: "rgba(15,23,42,0.9)",
              color: "#9ca3af",
              cursor: "pointer",
              fontSize: 16,
            }}
          >
            ×
          </button>
        </div>

        {/* Kleine mode-chip */}
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "3px 10px",
            borderRadius: 999,
            background: "rgba(31,41,55,0.9)",
            color: "#e5e7eb",
            fontSize: 11,
            marginBottom: 12,
          }}
        >
          <span
            style={{
              display: "inline-block",
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: isSignup ? "#22c55e" : "#4f46e5",
            }}
          />
          <span>{isSignup ? "Nieuw bij Bond" : "Terug bij Bond"}</span>
        </div>

        <h2
          style={{
            margin: "4px 0 14px",
            fontSize: 20,
            fontWeight: 600,
          }}
        >
          {title}
        </h2>

        <form
          onSubmit={handleSubmit}
          style={{ display: "flex", flexDirection: "column", gap: 10 }}
        >
          {/* Naam (username) */}
          <div style={{ textAlign: "left" }}>
            <label
              style={{ display: "block", fontSize: 13, marginBottom: 4 }}
            >
              Naam (username)
            </label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 10px",
                borderRadius: 999,
                border: "1px solid rgba(75,85,99,0.9)",
                background: "#020617",
                color: "#e5e7eb",
                fontSize: 13,
              }}
              placeholder="Bijv. RemiKoek"
            />
            <div
              style={{
                marginTop: 4,
                fontSize: 11,
                color: "#9ca3af",
              }}
            >
              Je username is zichtbaar voor je vrienden in Bond.
            </div>
          </div>

          {/* E-mailadres */}
          <div style={{ textAlign: "left" }}>
            <label
              style={{ display: "block", fontSize: 13, marginBottom: 4 }}
            >
              E-mailadres
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 10px",
                borderRadius: 999,
                border: "1px solid rgba(75,85,99,0.9)",
                background: "#020617",
                color: "#e5e7eb",
                fontSize: 13,
              }}
              placeholder="jij@example.com"
            />
          </div>

          {error && (
            <div
              style={{
                marginTop: 4,
                fontSize: 12,
                color: "#fca5a5",
                textAlign: "left",
              }}
            >
              {error}
            </div>
          )}

          {sent && !error && (
            <div
              style={{
                marginTop: 4,
                fontSize: 12,
                color: "#bbf7d0",
                textAlign: "left",
              }}
            >
              We hebben je een link gestuurd. Check je e-mail en klik op de link
              om door te gaan.
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            style={{
              marginTop: 12,
              padding: "9px 18px",
              borderRadius: 999,
              border: "none",
              background:
                "linear-gradient(135deg, #4f46e5 0%, #6366f1 40%, #ec4899 100%)",
              color: "#fff",
              fontSize: 14,
              fontWeight: 500,
              cursor: busy ? "default" : "pointer",
              opacity: busy ? 0.7 : 1,
              boxShadow: "0 12px 32px rgba(15,23,42,0.9)",
            }}
          >
            {busy ? "Bezig..." : buttonLabel}
          </button>
        </form>

        {/* Mode switch onderaan */}
        <div
          style={{
            marginTop: 14,
            fontSize: 12,
            color: "#9ca3af",
            textAlign: "center",
          }}
        >
          {isSignup ? (
            <>
              Heb je al een account?{" "}
              <button
                type="button"
                onClick={() => onSwitchMode("login")}
                style={{
                  border: "none",
                  background: "transparent",
                  color: "#e5e7eb",
                  cursor: "pointer",
                  textDecoration: "underline",
                  padding: 0,
                  fontSize: 12,
                }}
              >
                Log in met een nieuwe link
              </button>
            </>
          ) : (
            <>
              Nog geen account?{" "}
              <button
                type="button"
                onClick={() => onSwitchMode("signup")}
                style={{
                  border: "none",
                  background: "transparent",
                  color: "#e5e7eb",
                  cursor: "pointer",
                  textDecoration: "underline",
                  padding: 0,
                  fontSize: 12,
                }}
              >
                Maak je Bond-account
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
